// @flow
import React, { useState, useCallback, useContext } from "react";
import ButtonToolbar from "react-bootstrap/lib/ButtonToolbar";
import Button from "react-bootstrap/lib/Button";
import EAForm from "components/ExtraAttributes/EAForm";
import Formsy, { addValidationRule } from "formsy-react";
import { type SearchFormResultDTO } from "data/types";
import AttributeSearch from "./AttributeSearch";
import "components/Form/index.css";
import "./index.css";
import { getClassNames, isNotInvalid } from "data/utils";
import { LabelSizeContext } from "data/context";
import { useEffectOnce } from "react-use";
import { Global, css } from "@emotion/react";

const EASearchForm = (props) => {
    const labelWidth = useContext(LabelSizeContext);
    return (
        <>
            <Global
                styles={css`
                    :root {
                        --search-form-label-width: ${labelWidth}px;
                    }
                `}
            />
            <EAForm
                isEditMode={true}
                fieldComponentClass={AttributeSearch}
                {...props}
            />
        </>
    );
};

type Props = {
    /** searchForm definition */
    form: SearchFormResultDTO,
    /** Search button label */
    searchLabel: string,
    /** whether the Search button should be enabled */
    searchEnabled?: boolean,
    /** whether to show search operators */
    showSearchOperators?: boolean,
    // /** callback when user clicks the Cancel button */
    // onCancel: () => void,
    /** callback when user clicks the Search button */
    onSearch: (model: Object) => void,
    /** callback when an attribute is edited
     * @param {string} name unique name of attribute being changed
     * @param {any} value new value
     */
    onChange: (name: string, value: any) => void,
    /** callback when a validation is finished on the form (could be valid or invalid) */
    onValidated?: (isValid: boolean) => void,
};

const SearchForm = React.forwardRef(
    (
        {
            searchEnabled,
            onValidated,
            showSearchOperators,
            onSearch,
            onChange,
            form,
            searchLabel,
        }: Props,
        ref
    ) => {
        const [isValid, setIsValid] = useState(false);

        useEffectOnce(() => {
            /* istanbul ignore next */
            addValidationRule("isNotInvalid", isNotInvalid);
        });

        const handleValidation = useCallback(
            (valid) => {
                onValidated && onValidated(valid);
                setIsValid(!!valid);
            },
            [onValidated]
        );

        const handleValid = useCallback(
            () => handleValidation(true),
            [handleValidation]
        );

        /* istanbul ignore next */
        const handleInvalid = useCallback(
            () => handleValidation(false),
            [handleValidation]
        );

        return (
            <Formsy
                ref={ref}
                className={getClassNames(
                    "form-inline",
                    "ip-form",
                    "form-edit",
                    "search-form",
                    showSearchOperators === true ? null : "search-op-hidden"
                )}
                onValidSubmit={onSearch}
                /* istanbul ignore next */
                onValid={handleValid}
                onInvalid={handleInvalid}
            >
                <LabelSizeContext.Provider value={form.labelWidth || 125}>
                    <EASearchForm
                        key="ea-sf"
                        itemUri={form.itemUri}
                        layoutXml={form.layoutXml}
                        attributes={form.attributes}
                        onChange={onChange}
                    />
                </LabelSizeContext.Provider>
                <ButtonToolbar className="form-actions">
                    <Button
                        type="submit"
                        bsStyle={isValid ? "primary" : "default"}
                        disabled={!(isValid && searchEnabled)}
                        style={{ padding: "6px 30px" }}
                        data-test="searchFormSubmitButton"
                    >
                        {searchLabel}
                    </Button>
                </ButtonToolbar>
            </Formsy>
        );
    }
);
SearchForm.displayName = "SearchForm";

export default SearchForm;
